import React from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { ContactButton } from '../components/common/ContactButton'

export function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-12">
        <Link 
          to="/" 
          className="inline-flex items-center text-emerald-600 hover:text-emerald-700 mb-8"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Home
        </Link>

        <h1 className="text-4xl font-bold text-gray-900 mb-8">Privacy Policy</h1>
        
        <div className="prose prose-lg max-w-none">
          <p className="text-gray-600 mb-8">
            <strong>Effective Date:</strong> August 1, 2025<br />
            <strong>Last Updated:</strong> December 4, 2025
          </p>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Introduction</h2>
            <p className="text-gray-700 mb-4">
              Welcome to CYARoutes ("we," "our," or "us"). We respect your privacy and are committed to protecting your personal data. This privacy policy explains how we collect, use, and safeguard your information when you use our website and mobile applications.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Information We Collect</h2>
            
            <h3 className="text-xl font-semibold text-gray-800 mb-3">2.1 Information You Provide</h3>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li>Account information via Apple Sign-In (name, email), Google Sign-In (name, email, profile photo), or email/password authentication</li>
              <li>Routes you create, including GPS coordinates and descriptions</li>
              <li>Photos and content you upload</li>
              <li>Comments and ratings on routes</li>
              <li>Hazard reports you submit (location, type, description, photos)</li>
              <li>Club membership information</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">2.2 Information Collected Automatically</h3>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li>Device information (device type, operating system)</li>
              <li>Usage data (pages visited, features used)</li>
              <li>IP address (for security and analytics)</li>
              <li>Location data:
                <ul className="list-circle pl-6 mt-2">
                  <li><strong>When in use:</strong> For navigation, route planning, and hazard reporting</li>
                  <li><strong>Background location (iOS app):</strong> For continued turn-by-turn guidance when screen is locked during active navigation</li>
                  <li><strong>Control:</strong> Manage in iOS Settings → CYARoutes → Location or device settings</li>
                </ul>
              </li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">2.3 Strava Integration (Optional)</h3>
            <p className="text-gray-700 mb-4">When you choose to connect your Strava account, we collect:</p>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li>Your cycling activity history (rides only, not runs/walks)</li>
              <li>GPS route data (polylines) from your rides</li>
              <li>Activity dates, distances, and durations</li>
              <li>Your Strava profile name</li>
            </ul>
            <p className="text-gray-700 mb-4">
              <strong>How we use Strava data:</strong>
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li>Track which bike paths you've ridden</li>
              <li>Calculate your coverage progress on routes</li>
              <li>Display your rides as personal heat maps on the map</li>
              <li>Calculate leaderboard rankings</li>
            </ul>
            <p className="text-gray-700 mb-4">
              You can disconnect Strava at any time, which will remove your synced activity data from our servers.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">2.4 Third-Party Services</h3>
            <p className="text-gray-700 mb-4">We use the following third-party services that may collect data:</p>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li><strong>Mapbox:</strong> For map display and route visualization</li>
              <li><strong>Google Places:</strong> For location search and POI data</li>
              <li><strong>Firebase:</strong> For authentication and data storage</li>
              <li><strong>Strava:</strong> For activity sync via their API (see section 2.3)</li>
              <li><strong>Sentry:</strong> For error monitoring on web app (no personal data collected)</li>
              <li><strong>Firebase Crashlytics:</strong> For crash reports on mobile app (no personal data collected)</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. How We Use Your Information</h2>
            <p className="text-gray-700 mb-4">We use your information to:</p>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li>Provide and maintain our services</li>
              <li>Create and manage your account</li>
              <li>Display your routes and content to other users (based on your privacy settings)</li>
              <li>Send important service updates and notifications</li>
              <li>Improve our services and develop new features</li>
              <li>Ensure platform security and prevent abuse</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Data Sharing and Disclosure</h2>
            <p className="text-gray-700 mb-4">We do not sell your personal data. We share your information only in these circumstances:</p>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li><strong>With your consent:</strong> When you make routes public or join clubs</li>
              <li><strong>Service providers:</strong> With trusted third parties who help us operate our services</li>
              <li><strong>Legal requirements:</strong> When required by law or to protect rights and safety</li>
              <li><strong>Business transfers:</strong> In connection with a merger or acquisition</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Heat Maps & Visualization</h2>
            <h3 className="text-xl font-semibold text-gray-800 mb-3">Personal Heat Maps</h3>
            <p className="text-gray-700 mb-4">
              If you connect Strava, your rides are displayed as heat maps on the map, showing routes you've cycled. These personal heat maps are visible only to you.
            </p>
            <h3 className="text-xl font-semibold text-gray-800 mb-3">Community Heat Maps (Future)</h3>
            <p className="text-gray-700 mb-4">
              We may offer aggregated, anonymized heat maps showing popular cycling routes across the community. If we enable this feature:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li>Your individual routes will never be identifiable</li>
              <li>Data is aggregated across many users to show general cycling patterns</li>
              <li>You will be notified before any community features launch</li>
              <li>You can opt out at any time</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Data Security</h2>
            <p className="text-gray-700 mb-4">
              We implement appropriate technical and organizational measures to protect your personal data, including:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li>Encryption of data in transit and at rest</li>
              <li>Regular security assessments and updates</li>
              <li>Access controls and authentication</li>
              <li>Secure data storage using Firebase with configured security rules</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Your Rights and Choices</h2>
            <p className="text-gray-700 mb-4">You have the right to:</p>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li><strong>Access:</strong> Request a copy of your personal data</li>
              <li><strong>Correction:</strong> Update or correct your information</li>
              <li><strong>Deletion:</strong> Request deletion of your account and data</li>
              <li><strong>Portability:</strong> Export your routes and data</li>
              <li><strong>Opt-out:</strong> Unsubscribe from marketing communications</li>
              <li><strong>Privacy settings:</strong> Control who can see your routes and profile</li>
              <li><strong>Disconnect Strava:</strong> Remove your Strava connection and synced activity data at any time</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Data Retention</h2>
            <p className="text-gray-700 mb-4">
              We retain your personal data for as long as necessary to provide our services and comply with legal obligations. When you delete your account, we will delete or anonymize your personal data within 30 days, except where retention is required by law.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Children's Privacy</h2>
            <p className="text-gray-700 mb-4">
              Our services are not intended for children under 16. We do not knowingly collect personal data from children. If you believe we have collected data from a child, please contact us immediately.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. International Data Transfers</h2>
            <p className="text-gray-700 mb-4">
              Your data may be transferred to and processed in countries other than your own. We ensure appropriate safeguards are in place to protect your data in accordance with this privacy policy.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. Updates to This Policy</h2>
            <p className="text-gray-700 mb-4">
              We may update this privacy policy from time to time. We will notify you of any material changes by posting the new policy on this page and updating the "Last Updated" date.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">12. Contact Us</h2>
            <p className="text-gray-700 mb-4">
              If you have any questions about this privacy policy or our data practices, please contact us at:
            </p>
            <div className="bg-gray-100 p-4 rounded-lg">
              <p className="text-gray-700">
                <strong>Contact:</strong> <ContactButton subject="Privacy Policy Inquiry">Send us a message</ContactButton><br />
                <strong>Address:</strong> Melbourne, Australia<br />
                <strong>Data Protection Officer:</strong> Vincent Masci
              </p>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">13. GDPR Information (For EU Users)</h2>
            <p className="text-gray-700 mb-4">
              If you are located in the European Union, you have additional rights under the General Data Protection Regulation (GDPR):
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li>Legal basis for processing: Legitimate interests and consent</li>
              <li>Right to lodge a complaint with a supervisory authority</li>
              <li>Right to withdraw consent at any time</li>
              <li>Right to object to processing</li>
            </ul>
          </section>
        </div>
      </div>
    </div>
  )
}